import { useState, useEffect, useCallback } from "react";
import { api } from "../services/api";

// ─── Helper: convierte una clave VAPID base64url a Uint8Array ────────────────
// Implementación canónica requerida por PushManager.subscribe()
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ─── Detección de capacidades del navegador ──────────────────────────────────
function checkSupport() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

// ─── Detección de iOS ────────────────────────────────────────────────────────
function checkIsIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

// ─── Detección de modo standalone (PWA instalada) ───────────────────────────
function checkIsStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * useNotifications — hook para gestionar Web Push Notifications.
 *
 * Expone estado y acciones para suscribir, desuscribir y probar notificaciones.
 * Degrada gracefully si el navegador no soporta la API o si falta la VAPID key.
 */
export function useNotifications() {
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;

  // Si no hay VAPID key configurada, el hook se desactiva completamente
  const hasVapidKey = Boolean(vapidKey && vapidKey.trim().length > 0);

  if (!hasVapidKey) {
    console.warn(
      "[useNotifications] VITE_VAPID_PUBLIC_KEY no está configurada. " +
      "Las notificaciones push están desactivadas."
    );
  }

  const [isSupported]   = useState(() => checkSupport() && hasVapidKey);
  const [isStandalone]  = useState(() => checkIsStandalone());
  const [isIos]         = useState(() => checkIsIos());
  const [permission, setPermission]     = useState(() =>
    checkSupport() ? Notification.permission : "denied"
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading]           = useState(false);

  // Verificar si ya existe una suscripción activa al montar el hook
  useEffect(() => {
    if (!isSupported) return;

    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        setIsSubscribed(Boolean(subscription));
      })
      .catch((err) => {
        console.warn("[useNotifications] Error al verificar suscripción existente:", err);
      });
  }, [isSupported]);

  // ─── requestPermissionAndSubscribe ─────────────────────────────────────────
  // Pide permiso al usuario, crea la suscripción push y la registra en el backend.
  const requestPermissionAndSubscribe = useCallback(async () => {
    if (!isSupported) return false;

    try {
      setLoading(true);

      // Solicitar permiso de notificaciones
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== "granted") {
        console.info("[useNotifications] El usuario rechazó el permiso de notificaciones.");
        return false;
      }

      // Obtener el registration del service worker
      const registration = await navigator.serviceWorker.ready;

      // Crear la suscripción push con la VAPID public key
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      // Enviar la suscripción al backend para que pueda enviar mensajes
      await api.notifications.subscribe(subscription.toJSON());

      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error("[useNotifications] Error al suscribirse a notificaciones:", err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, vapidKey]);

  // ─── unsubscribe ────────────────────────────────────────────────────────────
  // Elimina la suscripción local del browser y notifica al backend para que
  // deje de enviar mensajes a este dispositivo.
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;

    try {
      setLoading(true);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        setIsSubscribed(false);
        return;
      }

      // Informar al backend antes de eliminar la suscripción local
      try {
        await api.notifications.unsubscribe(subscription.endpoint);
      } catch (apiErr) {
        // Si el backend falla, igual eliminamos la suscripción local
        console.warn("[useNotifications] El backend no pudo eliminar la suscripción:", apiErr);
      }

      await subscription.unsubscribe();
      setIsSubscribed(false);
    } catch (err) {
      console.error("[useNotifications] Error al desuscribirse:", err);
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // ─── sendTestNotification ───────────────────────────────────────────────────
  // Solicita al backend que envíe una notificación de prueba a este dispositivo.
  // Solo debe usarse en entornos de desarrollo.
  const sendTestNotification = useCallback(async () => {
    try {
      await api.notifications.test();
    } catch (err) {
      console.error("[useNotifications] Error al enviar notificación de prueba:", err);
    }
  }, []);

  return {
    isSupported,
    isIos,
    isStandalone,
    permission,
    isSubscribed,
    loading,
    requestPermissionAndSubscribe,
    unsubscribe,
    sendTestNotification,
  };
}
